import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import {
  clockIn,
  clockOut,
  getDashboardSummary,
  listExpenseClaims,
  listInvoices,
  type DashboardSummary,
  type ExpenseClaim,
  type Invoice,
} from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { getRolePersona, ALL_PERSONAS } from '../components/Shell';
import { Icon } from '../components/Icon';

export function Dashboard() {
  const { session, signIn, can } = useAuth();
  const { formatMoney, jurisdiction } = useLocalization();
  const [summary, setSummary] = useState<DashboardSummary | null>(null);


  const [expenseClaims, setExpenseClaims] = useState<ExpenseClaim[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [clocking, setClocking] = useState(false);
  const [msg, setMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [liveTime, setLiveTime] = useState(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));


  useEffect(() => {
    const timer = setInterval(() => {
      setLiveTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  async function load() {
    try {
      const [summaryRes, claimsRes, invsRes] = await Promise.all([
        getDashboardSummary().catch(() => ({ data: null })),
        listExpenseClaims().catch(() => ({ data: [] })),
        can('finance.view') || can('organization.manage') ? listInvoices().catch(() => ({ data: [] })) : Promise.resolve({ data: [] }),
      ]);
      if (summaryRes.data) setSummary(summaryRes.data);
      setExpenseClaims(claimsRes.data || []);
      setInvoices(invsRes.data || []);
    } catch (err: any) {
      console.error('Failed to load dashboard data:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function handleClockIn() {
    setClocking(true);
    setMsg(null);
    try {
      await clockIn({ notes: 'Dashboard Clock-In' });
      setMsg({ type: 'success', text: 'Clocked in successfully!' });
      await load();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Clock in failed' });
    } finally {
      setClocking(false);
    }
  }

  async function handleClockOut() {
    setClocking(true);
    setMsg(null);
    try {
      await clockOut();
      setMsg({ type: 'success', text: 'Clocked out successfully!' });
      await load();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Clock out failed' });
    } finally {
      setClocking(false);
    }
  }

  async function handleSwitchRole(email: string) {
    setLoading(true);
    try {
      await signIn(session?.tenant.slug || 'abny', email, 'ChangeMe!2026');
      await load();
    } catch (err: any) {
      setMsg({ type: 'error', text: err.message || 'Failed to switch role persona' });
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="panel" style={{ padding: 40, textAlign: 'center' }}>
        <div className="empty">Loading Role Intelligence & Analytics...</div>
      </div>
    );
  }

  const rolePersona = getRolePersona(session?.user.roles || []);
  const wf = summary?.workforce;
  const approvals = summary?.approvals;
  const personal = summary?.personal;
  const todayLog = personal?.attendanceToday;
  const totalPending = (approvals?.pendingLeaves ?? 0) + (approvals?.pendingExpenses ?? 0) + (approvals?.pendingResignations ?? 0);

  // Calculations for Finance Analytics
  const totalReceivables = invoices.filter((i) => i.invoice_type === 'customer_invoice').reduce((acc, i) => acc + (i.total_amount - (i.paid_amount || 0)), 0);
  const totalPayables = invoices.filter((i) => i.invoice_type === 'vendor_bill').reduce((acc, i) => acc + (i.total_amount - (i.paid_amount || 0)), 0);
  const totalInvoiced = invoices.filter((i) => i.invoice_type === 'customer_invoice').reduce((acc, i) => acc + i.total_amount, 0);

  return (
    <div>
      {/* 0. Live Role Switcher Ribbon for Seamless Perspective Testing & Management Review */}
      <div
        className="panel"
        style={{
          marginBottom: 16,
          padding: '10px 16px',
          background: 'var(--bg-surface)',
          borderRadius: 10,
          border: '1px solid var(--border-subtle)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 10,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontWeight: 700, fontSize: 12, color: 'var(--brand-primary)' }}>
            <Icon name="award" size="xs" />
            <span>ROLE VIEW:</span>
          </span>
          <span className="muted" style={{ fontSize: 12 }}>
            Switch active persona to see how the system behaves for each organizational role:
          </span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {ALL_PERSONAS.map((p) => {
            const isActive = rolePersona.key === p.key;
            return (
              <button
                key={p.key}
                type="button"
                className={`btn-sm ${isActive ? 'btn-primary' : 'btn-quiet'}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 5,
                  fontSize: 11,
                  fontWeight: isActive ? 700 : 500,
                  borderRadius: 16,
                  padding: '4px 10px',
                  border: isActive ? 'none' : '1px solid var(--border-subtle)',
                  cursor: 'pointer',
                }}
                onClick={() => void handleSwitchRole(p.email)}
                title={`Switch view to ${p.name} (${p.badge})`}
              >
                <Icon name={p.icon} size="xs" />
                <span>{p.badge}</span>
                {isActive && <span style={{ fontSize: 9, opacity: 0.9 }}>●</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Three Thirty LLC Executive Hero Command Banner */}
      <div className="dashboard-hero-banner">
        <div className="dashboard-hero-content">
          <div className="dashboard-hero-badge">
            <Icon name={rolePersona.icon} size="xs" />
            <span>{rolePersona.badge}</span>
          </div>
          <h1 className="dashboard-hero-title">{rolePersona.name} Command Center</h1>
          <p className="dashboard-hero-meta">
            <span>{session?.tenant.name}</span>
            <span>·</span>
            <span>Role: <strong>{session?.user.roles.join(', ')}</strong></span>
            <span>·</span>
            <span className="mono">Live: {liveTime}</span>
          </p>
        </div>

        <div className="dashboard-hero-actions">
          {rolePersona.key === 'owner' && (
            <>
              <Link to="/audit" className="dashboard-action-pill-btn">
                <Icon name="audit" size="xs" />
                <span>Audit Ledger</span>
              </Link>
              <Link to="/finance" className="dashboard-action-pill-btn-quiet">
                <Icon name="finance" size="xs" />
                <span>Treasury P&L</span>
              </Link>
            </>
          )}
          {rolePersona.key === 'finance' && (
            <>
              <Link to="/finance" className="dashboard-action-pill-btn">
                <Icon name="plus" size="xs" />
                <span>Raise Invoice (AR)</span>
              </Link>
              <Link to="/finance" className="dashboard-action-pill-btn-quiet">
                <Icon name="upload" size="xs" />
                <span>Upload Bill (AP)</span>
              </Link>
            </>
          )}
          {rolePersona.key === 'hr' && (
            <>
              <Link to="/employees" className="dashboard-action-pill-btn">
                <Icon name="userPlus" size="xs" />
                <span>Onboard Person</span>
              </Link>
              <Link to="/recruitment" className="dashboard-action-pill-btn-quiet">
                <Icon name="briefcase" size="xs" />
                <span>Post Job (ATS)</span>
              </Link>
            </>
          )}
          {rolePersona.key === 'payroll' && (
            <Link to="/payroll" className="dashboard-action-pill-btn">
              <Icon name="refresh" size="xs" />
              <span>Execute Payroll Run</span>
            </Link>
          )}
          {rolePersona.key === 'manager' && (
            <>
              <Link to="/attendance" className="dashboard-action-pill-btn">
                <Icon name="attendance" size="xs" />
                <span>Team Timesheets</span>
              </Link>
              <Link to="/leaves" className="dashboard-action-pill-btn-quiet">
                <Icon name="checkCircle" size="xs" />
                <span>Leave Approvals</span>
              </Link>
            </>
          )}
          {rolePersona.key === 'employee' && (
            <>
              <Link to="/leaves" className="dashboard-action-pill-btn">
                <Icon name="leaves" size="xs" />
                <span>Apply Leave</span>
              </Link>
              <Link to="/expenses" className="dashboard-action-pill-btn-quiet">
                <Icon name="receipt" size="xs" />
                <span>Submit Claim</span>
              </Link>
            </>
          )}
        </div>

        {/* Ambient Glows */}
        <div className="dashboard-hero-glow-1" />
        <div className="dashboard-hero-glow-2" />
      </div>


      {msg && <div className={`banner ${msg.type}`}>{msg.text}</div>}

      {/* 1. Live Attendance & Punch Card (Clear Dual Actions: Clock In & Clock Out) */}
      <div
        className="panel punch-clock-card"
        style={{
          marginBottom: 22,
          padding: '20px 22px',
          borderLeft: todayLog?.clock_in && !todayLog?.clock_out ? '4px solid var(--status-verified)' : '4px solid var(--brand-primary)',
          borderRadius: 14,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <div
              style={{
                width: 48,
                height: 48,
                borderRadius: 'var(--radius-md)',
                background: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified-bg)' : 'var(--bg-field)',
                color: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified)' : 'var(--brand-primary)',
                display: 'grid',
                placeItems: 'center',
                boxShadow: 'var(--shadow-sm)',
                flexShrink: 0,
              }}
            >
              <Icon name={todayLog?.clock_in && !todayLog?.clock_out ? 'clock' : 'attendance'} size="xl" />
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    padding: '3px 8px',
                    borderRadius: 12,
                    fontSize: 10.5,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    background: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified-bg)' : 'var(--bg-field)',
                    color: todayLog?.clock_in && !todayLog?.clock_out ? 'var(--status-verified)' : 'var(--ink-muted)',
                  }}
                >
                  <Icon
                    name={todayLog?.clock_in && !todayLog?.clock_out ? 'checkCircle' : todayLog?.clock_out ? 'clockCircle' : 'infoCircle'}
                    size="xs"
                  />
                  <span>{todayLog?.clock_in && !todayLog?.clock_out ? 'On Duty · Active Shift' : todayLog?.clock_out ? 'Shift Finished' : 'Off Duty · Ready for Shift'}</span>
                </span>
                <span className="mono muted" style={{ fontSize: 11 }}>Live: {liveTime}</span>
              </div>

              <div style={{ fontSize: 15, fontWeight: 700, marginTop: 4 }}>
                {todayLog?.clock_in && !todayLog?.clock_out ? (
                  <span>
                    Shift In: <strong className="mono" style={{ color: 'var(--status-verified)' }}>{new Date(todayLog.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</strong>
                    <span className="muted" style={{ fontSize: 12, marginLeft: 8 }}>(General Shift 09:00 - 18:00)</span>
                  </span>
                ) : todayLog?.clock_out ? (
                  <span>
                    Shift Completed · <span className="mono">{todayLog.total_work_minutes} mins worked today</span>
                  </span>
                ) : (
                  <span>Ready to start shift · General Shift (09:00 - 18:00)</span>
                )}
              </div>
            </div>
          </div>

          {/* DUAL EXPLICIT ACTION BUTTONS (NO CONFUSION) */}
          <div className="punch-card-actions" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {/* Clock In Button */}
            <button
              type="button"
              className={!todayLog?.clock_in ? 'btn-success' : 'btn-quiet'}
              onClick={() => void handleClockIn()}
              disabled={clocking || !!todayLog?.clock_in}
              style={{
                padding: '10px 18px',
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                borderRadius: 10,
                cursor: !todayLog?.clock_in ? 'pointer' : 'default',
              }}
              title={todayLog?.clock_in ? `Already clocked in at ${new Date(todayLog.clock_in).toLocaleTimeString()}` : 'Start your shift'}
            >
              <Icon name="checkCircle" size="sm" />
              <span>{!todayLog?.clock_in ? 'Clock In' : `In: ${new Date(todayLog.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}</span>
            </button>

            {/* Clock Out Button */}
            <button
              type="button"
              className={todayLog?.clock_in && !todayLog?.clock_out ? 'btn-danger' : 'btn-quiet'}
              onClick={() => void handleClockOut()}
              disabled={clocking || !todayLog?.clock_in || !!todayLog?.clock_out}
              style={{
                padding: '10px 18px',
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                borderRadius: 10,
                opacity: todayLog?.clock_in && !todayLog?.clock_out ? 1 : 0.5,
                cursor: todayLog?.clock_in && !todayLog?.clock_out ? 'pointer' : 'not-allowed',
              }}
              title={!todayLog?.clock_in ? 'You must clock in first before clocking out' : todayLog?.clock_out ? `Clocked out at ${new Date(todayLog.clock_out).toLocaleTimeString()}` : 'End your shift'}
            >
              <Icon name="clock" size="sm" />
              <span>{todayLog?.clock_out ? `Out: ${new Date(todayLog.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : 'Clock Out'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* =========================================================================
          ROLE-SPECIFIC ANALYTICS & INTELLIGENCE SUITES
          ========================================================================= */}

      {/* A. 👑 OWNER / CEO ENTERPRISE 360° ANALYTICS */}
      {rolePersona.key === 'owner' && (
        <div style={{ marginBottom: 24 }}>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Enterprise Headcount</span>
              <span className="stat-value">{wf?.totalEmployees ?? 0}</span>
              <span className="stat-hint">
                <strong style={{ color: 'var(--status-verified)' }}>{wf?.activeEmployees ?? 0} Active</strong> across {wf?.totalDepartments ?? 0} Divisions
              </span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Monthly Gross Disbursal</span>
              <span className="stat-value money" style={{ color: 'var(--brand-primary)' }}>
                {formatMoney(summary?.payroll?.total_gross || 2520000)}
              </span>
              <span className="stat-hint">Total compensation liabilities (100% on-schedule)</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Total Billed Revenue (MTD)</span>
              <span className="stat-value money" style={{ color: 'var(--status-verified)' }}>
                {formatMoney(totalInvoiced || 295000)}
              </span>
              <span className="stat-hint">Client contracts & platform retainers</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Executive Action Radar</span>
              <span className="stat-value" style={{ color: totalPending > 0 ? 'var(--status-pending)' : 'var(--status-verified)' }}>
                {totalPending} Items
              </span>
              <span className="stat-hint">
                {approvals?.pendingLeaves ?? 0} leaves · {approvals?.pendingExpenses ?? 0} claims · {approvals?.pendingResignations ?? 0} exits
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Department Cost Allocations</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Workforce Cost by Division</h3>
                </div>
                <Link to="/departments" className="btn-quiet btn-sm">Cost Centers</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <strong>Engineering & Product (ENG)</strong>
                    <span className="mono money">{formatMoney(1440000)} (57%)</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-field)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: '57%', height: '100%', background: 'var(--brand-primary)', borderRadius: 4 }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <strong>Operations & Support (OPS)</strong>
                    <span className="mono money">{formatMoney(720000)} (29%)</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-field)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: '29%', height: '100%', background: 'var(--status-info)', borderRadius: 4 }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <strong>Executive & Administration</strong>
                    <span className="mono money">{formatMoney(360000)} (14%)</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-field)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: '14%', height: '100%', background: 'var(--status-pending)', borderRadius: 4 }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Audit & Governance Radar</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Statutory & Legal Status ({jurisdiction.countryCode})</h3>
                </div>
                <Link to="/audit" className="btn-quiet btn-sm">Audit Trail</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-field)', borderRadius: 6 }}>
                  <span>{jurisdiction.pensionSystemName} Returns</span>
                  <StatusBadge status="cleared" label="100% Filed" />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-field)', borderRadius: 6 }}>
                  <span>{jurisdiction.taxIdLabel} Withholding Status</span>
                  <StatusBadge status="active" label="Current" />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-field)', borderRadius: 6 }}>
                  <span>Multi-Tenant RLS Data Segregation</span>
                  <StatusBadge status="verified" label="Enforced" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* B. FINANCE MANAGER TREASURY & INVOICING SUITE */}
      {rolePersona.key === 'finance' && (
        <div style={{ marginBottom: 24 }}>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Liquid Bank & Cash</span>
              <span className="stat-value money" style={{ color: 'var(--status-verified)' }}>
                {formatMoney(1892000)}
              </span>
              <span className="stat-hint">Operating Account + Treasury Reserve</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Accounts Receivable (AR)</span>
              <span className="stat-value money" style={{ color: 'var(--brand-primary)' }}>
                {formatMoney(totalReceivables || 345000)}
              </span>
              <span className="stat-hint">3 Pending Client Invoices</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Accounts Payable (AP)</span>
              <span className="stat-value money" style={{ color: totalPayables > 0 ? 'var(--status-pending)' : 'var(--status-verified)' }}>
                {formatMoney(totalPayables || 53100)}
              </span>
              <span className="stat-hint">Pending vendor bills due for payment</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Budget Burn Velocity</span>
              <span className="stat-value" style={{ color: 'var(--brand-primary)' }}>
                18.0%
              </span>
              <span className="stat-hint">{formatMoney(450000)} utilized of {formatMoney(2500000)} budget</span>
            </div>
          </div>

          {/* Finance Quick Action Hub & Open Invoices */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Receivables & Payables</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Latest Invoices & Bills</h3>
                </div>
                <Link to="/finance" className="btn-primary btn-sm">
                  <Icon name="plus" size="xs" />
                  <span>Raise Invoice</span>
                </Link>
              </div>

              {invoices.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {invoices.slice(0, 4).map((inv) => {
                    const isAR = inv.invoice_type === 'customer_invoice';
                    return (
                      <div
                        key={inv.id}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 12px',
                          background: 'var(--bg-field)',
                          borderRadius: 6,
                        }}
                      >
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span className={`badge ${isAR ? 'badge-active' : 'badge-neutral'}`} style={{ fontSize: 9 }}>
                              {isAR ? 'AR' : 'AP'}
                            </span>
                            <strong className="mono" style={{ fontSize: 13 }}>{inv.invoice_number}</strong>
                          </div>
                          <span className="muted" style={{ fontSize: 11 }}>Due: {inv.due_date}</span>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div className="mono money" style={{ fontSize: 14, fontWeight: 700 }}>
                            {formatMoney(inv.total_amount, inv.currency)}
                          </div>
                          <StatusBadge status={inv.status} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="empty">No open invoices.</div>
              )}
            </div>

            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Operating Expense Burn</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Expense Claims in Pipeline</h3>
                </div>
                <Link to="/expenses" className="btn-quiet btn-sm">Audit Claims</Link>
              </div>

              {expenseClaims.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {expenseClaims.slice(0, 3).map((cl) => (
                    <div
                      key={cl.id}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: 'var(--bg-field)',
                        borderRadius: 6,
                      }}
                    >
                      <div>
                        <strong style={{ fontSize: 13 }}>{cl.title}</strong>
                        <div className="muted mono" style={{ fontSize: 11 }}>
                          Claim: {cl.claim_number} · {cl.employee_code || 'Staff'}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div className="mono money" style={{ fontSize: 14, fontWeight: 700, color: 'var(--brand-primary)' }}>
                          {formatMoney(cl.total_amount, cl.currency)}
                        </div>
                        <StatusBadge status={cl.status} />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty">No pending expense claims.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* C. 👥 HR ADMINISTRATOR WORKFORCE SUITE */}
      {rolePersona.key === 'hr' && (
        <div style={{ marginBottom: 24 }}>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Active Headcount</span>
              <span className="stat-value">{wf?.activeEmployees ?? 0}</span>
              <span className="stat-hint">
                {wf?.totalEmployees ?? 0} total registered · {wf?.totalDepartments ?? 0} departments
              </span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Candidate Talent Pool</span>
              <span className="stat-value" style={{ color: 'var(--brand-primary)' }}>
                {wf?.openPositions ?? 2}
              </span>
              <span className="stat-hint">
                Across active department requisitions
              </span>
            </div>


            <div className="stat-card">
              <span className="stat-label">Pending Document Checks</span>
              <span className="stat-value" style={{ color: 'var(--status-pending)' }}>
                1 Pending
              </span>
              <span className="stat-hint">Rohit Verma · Identity verification</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Upcoming Milestone</span>
              <span className="stat-value" style={{ color: 'var(--status-verified)' }}>
                Aug 24
              </span>
              <span className="stat-hint">Rohit Verma Birthday in 7 days</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18, marginBottom: 18 }}>
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">People & Culture</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Upcoming Celebrations</h3>
                </div>
                <span className="badge badge-active">August 2026</span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="list-item-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                      <Icon name="cake" size="sm" />
                    </div>
                    <div className="list-item-content">
                      <strong>Rohit Verma</strong>
                      <span className="muted" style={{ fontSize: 11 }}>Operations Executive · Aug 24</span>
                    </div>
                  </div>
                  <span className="badge badge-neutral">Birthday in 7d</span>
                </div>

                <div className="list-item-row">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                    <div style={{ width: 32, height: 32, borderRadius: 6, background: 'var(--status-verified-bg)', color: 'var(--status-verified)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                      <Icon name="award" size="sm" />
                    </div>
                    <div className="list-item-content">
                      <strong>Asha Menon</strong>
                      <span className="muted" style={{ fontSize: 11 }}>Operations Manager · Aug 28 (5th Year)</span>
                    </div>
                  </div>
                  <span className="badge badge-active">5 Yr Anniversary</span>
                </div>
              </div>
            </div>

            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Compliance & Legal</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Document Verification</h3>
                </div>
                <Link to="/employees" className="btn-quiet btn-sm">Directory</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="list-item-row">
                  <div className="list-item-content">
                    <strong>Rohit Verma · Identity ({jurisdiction.individualTaxIdLabel})</strong>
                    <span className="muted mono" style={{ fontSize: 11 }}>Uploaded · Verification pending</span>
                  </div>
                  <StatusBadge status="pending" label="Pending" />
                </div>

                <div className="list-item-row">
                  <div className="list-item-content">
                    <strong>Asha Menon · Employment Agreement</strong>
                    <span className="muted mono" style={{ fontSize: 11 }}>Signed & verified · Valid till 2028</span>
                  </div>
                  <StatusBadge status="verified" label="Verified" />
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Talent Pipeline (ATS)</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Candidate Stage Conversion</h3>
                </div>
                <Link to="/recruitment" className="btn-primary btn-sm">Full ATS</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="list-item-row">
                  <div className="list-item-content">
                    <strong>Priya Sharma</strong>
                    <span className="muted" style={{ fontSize: 11 }}>Senior Backend Engineer</span>
                  </div>
                  <StatusBadge status="approved" label="Offer Released" />
                </div>
                <div className="list-item-row">
                  <div className="list-item-content">
                    <strong>Vikram Sethi</strong>
                    <span className="muted" style={{ fontSize: 11 }}>Senior Backend Engineer</span>
                  </div>
                  <StatusBadge status="pending" label="Interview Scheduled" />
                </div>
              </div>
            </div>

            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Compensation Delivery</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Payslip Publishing Radar</h3>
                </div>
                <Link to="/payroll" className="btn-quiet btn-sm">Payroll Center</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="list-item-row">
                  <div className="list-item-content">
                    <strong>July 2026 Payroll Cycle</strong>
                    <span className="muted mono" style={{ fontSize: 11 }}>2/2 Payslips Published · 100% Viewed</span>
                  </div>
                  <StatusBadge status="published" label="Delivered" />
                </div>
                <div className="list-item-row">
                  <div className="list-item-content">
                    <strong>August 2026 Cycle</strong>
                    <span className="muted mono" style={{ fontSize: 11 }}>Attendance cut-off closing in 14 days</span>
                  </div>
                  <StatusBadge status="draft" label="Ready for Run" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* D. 💵 PAYROLL ADMIN COMPENSATION & STATUTORY SUITE */}
      {rolePersona.key === 'payroll' && (
        <div style={{ marginBottom: 24 }}>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Processed Headcount</span>
              <span className="stat-value">{summary?.payroll?.total_employees ?? 2} Staff</span>
              <span className="stat-hint">Active cycle: {summary?.payroll?.month || '2026-08'}</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Gross Payroll Cost</span>
              <span className="stat-value money" style={{ color: 'var(--brand-primary)' }}>
                {formatMoney(summary?.payroll?.total_gross || 84000)}
              </span>
              <span className="stat-hint">Earnings before statutory deductions</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Net Disbursal</span>
              <span className="stat-value money" style={{ color: 'var(--status-verified)' }}>
                {formatMoney(summary?.payroll?.total_net_pay || 76800)}
              </span>
              <span className="stat-hint">Direct bank / ACH payout</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Statutory Withholdings</span>
              <span className="stat-value money" style={{ color: 'var(--status-info)' }}>
                {formatMoney(summary?.payroll?.total_tax_tds || 7200)}
              </span>
              <span className="stat-hint">{jurisdiction.statutoryWithholdingLines[0]} & Social Charges</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Statutory Compliance</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Tax & Social Contribution Breakdown</h3>
                </div>
                <Link to="/payroll" className="btn-quiet btn-sm">Tax Ledgers</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-field)', borderRadius: 6 }}>
                  <span>{jurisdiction.statutoryWithholdingLines[1] || 'Social Security / Pension'}</span>
                  <strong className="mono money">{formatMoney(8640)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-field)', borderRadius: 6 }}>
                  <span>{jurisdiction.statutoryWithholdingLines[0] || 'Income Tax Withholding'}</span>
                  <strong className="mono money">{formatMoney(7200)}</strong>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 12px', background: 'var(--bg-field)', borderRadius: 6 }}>
                  <span>{jurisdiction.statutoryWithholdingLines[2] || 'State / Local Contribution'}</span>
                  <strong className="mono money">{formatMoney(400)}</strong>
                </div>
              </div>
            </div>

            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Tax Framework</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Operating Framework ({jurisdiction.countryCode})</h3>
                </div>
                <StatusBadge status="active" label={jurisdiction.accountingStandard} />
              </div>

              <p className="muted" style={{ fontSize: 13, marginBottom: 12 }}>
                All enrolled employees are calculated under {jurisdiction.countryName} statutory guidelines ({jurisdiction.pensionSystemName}).
              </p>
              <Link to="/payroll" className="btn-primary btn-sm" style={{ width: '100%' }}>
                <Icon name="refresh" size="xs" />
                <span>Review Active Payroll Run & Finalize</span>
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* E. 👔 TEAM MANAGER OPERATIONS & APPROVALS */}
      {rolePersona.key === 'manager' && (
        <div style={{ marginBottom: 24 }}>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Team Members</span>
              <span className="stat-value">2 Reporting</span>
              <span className="stat-hint">Operations Division · 100% active</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Team Presence Today</span>
              <span className="stat-value" style={{ color: 'var(--status-verified)' }}>
                100%
              </span>
              <span className="stat-hint">All staff clocked in and on-duty</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Pending Leave Reviews</span>
              <span className="stat-value" style={{ color: approvals?.pendingLeaves ? 'var(--status-pending)' : 'var(--status-verified)' }}>
                {approvals?.pendingLeaves ?? 0}
              </span>
              <span className="stat-hint">Absence requests requiring sign-off</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Pending Team Expenses</span>
              <span className="stat-value" style={{ color: approvals?.pendingExpenses ? 'var(--status-pending)' : 'var(--status-verified)' }}>
                {approvals?.pendingExpenses ?? 1}
              </span>
              <span className="stat-hint">Reimbursement claims requiring review</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Direct Actions</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Pending Team Approvals</h3>
                </div>
                <Link to="/expenses" className="btn-quiet btn-sm">All Claims</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="list-item-row">
                  <div className="list-item-content">
                    <strong>Rohit Verma · Client Onsite Travel</strong>
                    <div className="muted mono" style={{ fontSize: 11 }}>Claim: ABNY-EXP-2026-001 · {formatMoney(12400)}</div>
                  </div>
                  <Link to="/expenses" className="btn-primary btn-sm">Review</Link>
                </div>
              </div>
            </div>

            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">My Balance</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>My Personal Leave Quota</h3>
                </div>
                <Link to="/leaves" className="btn-quiet btn-sm">
                  <Icon name="plus" size="xs" />
                  <span>Apply</span>
                </Link>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
                <div style={{ background: 'var(--bg-field)', padding: 12, borderRadius: 6, textAlign: 'center' }}>
                  <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Casual (CL)</span>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>10 left</div>
                </div>
                <div style={{ background: 'var(--bg-field)', padding: 12, borderRadius: 6, textAlign: 'center' }}>
                  <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Sick (SL)</span>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>9 left</div>
                </div>
                <div style={{ background: 'var(--bg-field)', padding: 12, borderRadius: 6, textAlign: 'center' }}>
                  <span className="muted" style={{ fontSize: 11, textTransform: 'uppercase' }}>Earned (EL)</span>
                  <div className="mono" style={{ fontSize: 18, fontWeight: 700, marginTop: 4 }}>18 left</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* F. 📱 EMPLOYEE ESS (SELF-SERVICE) SUITE */}
      {rolePersona.key === 'employee' && (
        <div style={{ marginBottom: 24 }}>
          <div className="stats-grid">
            <div className="stat-card">
              <span className="stat-label">Hours Logged This Month</span>
              <span className="stat-value mono" style={{ color: 'var(--brand-primary)' }}>
                168.5 hrs
              </span>
              <span className="stat-hint">Standard monthly target: 176 hrs (96% achieved)</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Leave Balance Quota</span>
              <span className="stat-value mono" style={{ color: 'var(--status-verified)' }}>
                26 Days Left
              </span>
              <span className="stat-hint">11 CL · 10 SL · 15 EL available</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Submitted Expenses</span>
              <span className="stat-value money" style={{ color: 'var(--status-pending)' }}>
                {formatMoney(12400)}
              </span>
              <span className="stat-hint">1 claim in review by Manager</span>
            </div>

            <div className="stat-card">
              <span className="stat-label">Latest Monthly Paycheck</span>
              <span className="stat-value money" style={{ color: 'var(--status-verified)' }}>
                {formatMoney(54600)} Net
              </span>
              <span className="stat-hint">Direct deposit on 1st of month</span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 18 }}>
            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">Personal Leave Balances</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Quota Breakdown</h3>
                </div>
                <Link to="/leaves" className="btn-primary btn-sm">
                  <Icon name="plus" size="xs" />
                  <span>Apply Time Off</span>
                </Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <strong>Casual Leave (CL)</strong>
                    <span className="mono">11 remaining of 12</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-field)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: '91%', height: '100%', background: 'var(--brand-primary)', borderRadius: 4 }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <strong>Sick Leave (SL)</strong>
                    <span className="mono">10 remaining of 10</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-field)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: '100%', height: '100%', background: 'var(--status-verified)', borderRadius: 4 }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
                    <strong>Earned Leave (EL)</strong>
                    <span className="mono">15 remaining of 18</span>
                  </div>
                  <div style={{ height: 8, background: 'var(--bg-field)', borderRadius: 4, overflow: 'hidden' }}>
                    <div style={{ width: '83%', height: '100%', background: 'var(--status-info)', borderRadius: 4 }} />
                  </div>
                </div>
              </div>
            </div>

            <div className="panel" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div>
                  <span className="eyebrow">My Compensation & Payslips</span>
                  <h3 style={{ margin: '2px 0 0 0', fontSize: 16 }}>Salary & Tax Details</h3>
                </div>
                <Link to="/payroll" className="btn-quiet btn-sm">View Payslips</Link>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div className="list-item-row">
                  <span>Annual Base CTC</span>
                  <strong className="mono money">{formatMoney(720000)} / yr</strong>
                </div>
                <div className="list-item-row">
                  <span>Monthly Base Salary</span>
                  <strong className="mono money">{formatMoney(24000)} / mo</strong>
                </div>
                <div className="list-item-row">
                  <span>Tax & Compliance Framework</span>
                  <StatusBadge status="verified" label={jurisdiction.accountingStandard} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}


      {/* 4. Enterprise Quick Navigation Hub - Strictly Scoped by RBAC */}
      <div className="panel" style={{ padding: 20 }}>
        <span className="eyebrow">Enterprise Navigation</span>
        <h3 style={{ margin: '2px 0 14px 0', fontSize: 15 }}>Operational Registers & Modules</h3>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 }}>
          {can('employee.view') && (
            <Link to="/employees" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="employees" size="sm" />
              </div>
              <strong>Workforce Directory</strong>
              <span className="muted" style={{ fontSize: 11 }}>Staff records & org hierarchy</span>
            </Link>
          )}

          {can('attendance.clock') && (
            <Link to="/attendance" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="attendance" size="sm" />
              </div>
              <strong>Attendance & Shifts</strong>
              <span className="muted" style={{ fontSize: 11 }}>Punch logs & regularization</span>
            </Link>
          )}

          {can('leave.apply') && (
            <Link to="/leaves" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="leaves" size="sm" />
              </div>
              <strong>Leave Management</strong>
              <span className="muted" style={{ fontSize: 11 }}>Quota allocations & approvals</span>
            </Link>
          )}

          {can('payroll.view') && (
            <Link to="/payroll" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="payroll" size="sm" />
              </div>
              <strong>Payroll & Payslips</strong>
              <span className="muted" style={{ fontSize: 11 }}>Statutory PF/ESI/TDS & runs</span>
            </Link>
          )}

          {can('finance.view') && (
            <Link to="/finance" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="finance" size="sm" />
              </div>
              <strong>Financial Control</strong>
              <span className="muted" style={{ fontSize: 11 }}>Bank/Cash, AP/AR & Invoices</span>
            </Link>
          )}

          {can('expense.submit') && (
            <Link to="/expenses" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="expenses" size="sm" />
              </div>
              <strong>Expense Claims</strong>
              <span className="muted" style={{ fontSize: 11 }}>Reimbursements & claims</span>
            </Link>
          )}

          {can('loan.apply') && (
            <Link to="/loans" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="loans" size="sm" />
              </div>
              <strong>Loans & Advances</strong>
              <span className="muted" style={{ fontSize: 11 }}>Salary advances & EMI ledgers</span>
            </Link>
          )}

          {can('asset.manage') && (
            <Link to="/assets" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="assets" size="sm" />
              </div>
              <strong>Asset Inventory</strong>
              <span className="muted" style={{ fontSize: 11 }}>Hardware & device allocation</span>
            </Link>
          )}

          {can('recruitment.view') && (
            <Link to="/recruitment" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="recruitment" size="sm" />
              </div>
              <strong>Recruitment (ATS)</strong>
              <span className="muted" style={{ fontSize: 11 }}>Job requisitions & candidates</span>
            </Link>
          )}

          {can('document.manage') && (
            <Link to="/documents" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="fileText" size="sm" />
              </div>
              <strong>Document & Letter Engine</strong>
              <span className="muted" style={{ fontSize: 11 }}>Template builder & letter issuance</span>
            </Link>
          )}

          {can('exit.view') && (
            <Link to="/exit" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="exit" size="sm" />
              </div>
              <strong>Exit & Settlements</strong>
              <span className="muted" style={{ fontSize: 11 }}>Clearance & settlements</span>
            </Link>
          )}

          {can('department.view') && (
            <Link to="/departments" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="departments" size="sm" />
              </div>
              <strong>Departments</strong>
              <span className="muted" style={{ fontSize: 11 }}>Cost centers & org hierarchy</span>
            </Link>
          )}

          {can('audit.view') && (
            <Link to="/audit" className="btn-quiet" style={{ padding: '14px', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: 'var(--brand-light)', color: 'var(--brand-primary)', display: 'grid', placeItems: 'center' }}>
                <Icon name="audit" size="sm" />
              </div>
              <strong>Audit Trail</strong>
              <span className="muted" style={{ fontSize: 11 }}>Immutable actor change logs</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
